/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import PersonnelActions from 'common/redux/actions/personnelMaster';
import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import {
    MasterPersonnel,
    AccessTypeEnum
} from 'common/models/internal/MasterPersonnel';
import { personnelMaster as personnelApi } from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import {
    isMultiTimeZoneSupportEnabled,
    ClientSelectors
} from 'common/redux/selectors/clientSelector';
import type { TFunction } from 'react-i18next';
import { ENTITY_NAME_ID } from 'common/constants/entity/personnelMaster';

import { RoleId } from 'common/models/internal/RoleId';
import { fetchRoles } from 'common/hooks/useRoles';

import PersonnelLanguageInput from './PersonnelLanguageInput';
import TransporterInput, { fetchTransporters } from './TransporterInput';
import PersonnelTypeModalSelector from './PersonnelTypeModalSelector';
import PersonnelTypeQuerySelector from './PersonnelTypeQuerySelector';
import { isTransporterPersonnelEnabled } from './utils';
import { PersonnelTypeEnum } from 'common/enums/PersonnelEnums';
import { Flex } from '@locus-taxy/neo-ui';
import { dashboardColors } from 'common/enums/ColorEnum';
import { LinkButton, ButtonTypes } from 'common/components/core/button';
import personnelMasterIds from 'common/tests/masterManagement/personnelMaster.json';
import { CountryHelper } from 'common/helpers/CountryHelper';
import { useTranslation } from 'react-i18next';
import useClientId from 'common/hooks/useClientId';
import customHistory from 'common/middleware/history';

type GetBooleanExtraPropsInput = {
    field: { value: boolean },
    t: TFunction
};

type GetBooleanExtraPropsOutput = { label: string };

const getBooleanExtraProps = ({
    field: { value },
    t
}: GetBooleanExtraPropsInput): GetBooleanExtraPropsOutput => ({
    label: value
        ? t('masterPersonnelForm.boolean.yes')
        : t('masterPersonnelForm.boolean.no')
});

const GotoRoleId = () => {
    const { t } = useTranslation('entityManagement');
    const clientId = useClientId();

    const url = customHistory.createHref({
        pathname: `client/${clientId}/settings/roles`
    });
    return (
        <Flex justifyContent="flex-end">
            <LinkButton
                semibold
                uppercase
                size="small"
                color={dashboardColors.darkSkyBlue.name}
                buttonType={ButtonTypes.Link}
                label={t('masterPersonnelProperties.gotoManageRoles')}
                target="_blank"
                href={url}
            />
        </Flex>
    );
};

const PERSONNEL_FIELDS = [
    {
        accessor: 'code',
        label: 'masterPersonnelForm.code',
        helpText: 'masterPersonnelForm.codeTooltip',
        placeholder: 'masterPersonnelForm.codePlaceholder',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        testId: personnelMasterIds.addNewPersonnel.inputCode,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.get('id'),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            }
            if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'masterPersonnelForm.name',
        testId: personnelMasterIds.addNewPersonnel.inputName,
        placeholder: 'masterPersonnelForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'phone.phoneNumber',
        label: 'masterPersonnelForm.phoneNumber',
        testId: personnelMasterIds.addNewPersonnel.inputPhoneNumber,
        type: FieldTypeEnum.Phone
    },
    {
        accessor: 'email.emailAddress',
        label: 'masterPersonnelForm.emailAddress',
        placeholder: 'masterPersonnelForm.emailAddressPlaceholder',
        testId: personnelMasterIds.addNewPersonnel.inputEmailAddress,
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'customRole',
        label: 'masterPersonnelForm.customRole',
        testId: personnelMasterIds.addNewPersonnel.inputCustomRole,
        placeholder: 'masterPersonnelForm.customRolePlaceholder',
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'language',
        label: 'masterPersonnelForm.languageName',
        placeholder: 'masterPersonnelForm.languageNamePlaceholder',
        component: PersonnelLanguageInput,
        extraProps: ({ entity, field }) => {
            const { placeholder } = field;
            const { language } = entity;
            return { value: language, placeholder };
        }
    },
    {
        accessor: 'transporters',
        label: 'masterPersonnelForm.transporterId',
        placeholder: 'masterPersonnelForm.transporterIdPlaceholder',
        component: TransporterInput,
        required: true,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('transporters', { personnelType, entitySettings }),
        extraProps: ({ field, props }) => {
            return {
                value: field.value,
                clientId: props.clientId,
                teams: props.teams,
                error: field.validationMessage,
                transporters: field.fetchInfo.response || []
            };
        },
        fetchInfo: {
            fetcher: ({ clientId }) => {
                return fetchTransporters({
                    clientId,
                    includeInactive: true
                });
            },
            params: ({ props }) => {
                return {
                    clientId: props.clientId
                };
            },
            response: null
        }
    },
    {
        accessor: 'hasAllTeamsAccess',
        required: true,
        label: 'masterPersonnelForm.hasAllTeamsAccess',
        testId: personnelMasterIds.addNewPersonnel.inputHasAllTeamsAccess,
        type: FieldTypeEnum.Boolean,
        dependentAccessors: ['teams'],
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('hasAllTeamsAccess', {
                personnelType,
                entitySettings
            }),
        extraProps: getBooleanExtraProps
    },
    {
        accessor: 'teams',
        label: 'masterPersonnelForm.teams',
        placeholder: 'masterPersonnelForm.teamsPlaceHolder',
        testId: personnelMasterIds.addNewPersonnel.inputTeams,
        type: FieldTypeEnum.Teams,
        helpText: 'masterPersonnelForm.teamsTooltip',
        dependentAccessors: ['hasAllTeamsAccess'],
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('teams', { personnelType, entitySettings }),
        extraProps: ({ entity }) => ({
            disabled: entity.hasAllTeamsAccess
        })
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => ({
            settingsName: 'personnelSettings'
        })
    },
    {
        accessor: 'canManageEntities',
        required: true,
        label: 'masterPersonnelForm.canManageEntities',
        testId: personnelMasterIds.addNewPersonnel.inputCanManageEntities,
        type: FieldTypeEnum.Boolean,
        extraProps: getBooleanExtraProps,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('canManageEntities', {
                personnelType,
                entitySettings
            })
    },
    {
        accessor: 'canVerifyEntities',
        required: true,
        label: 'masterPersonnelForm.canVerifyEntities',
        testId: personnelMasterIds.addNewPersonnel.inputCanVerifyEntities,
        type: FieldTypeEnum.Boolean,
        extraProps: getBooleanExtraProps,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('canVerifyEntities', {
                personnelType,
                entitySettings
            })
    },
    {
        accessor: 'canManagePersonnel',
        required: true,
        testId: personnelMasterIds.addNewPersonnel.inputCanManagePersonnel,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('canManagePersonnel', {
                personnelType,
                entitySettings
            }),
        label: 'masterPersonnelForm.canManagePersonnel',
        type: FieldTypeEnum.Boolean,
        extraProps: getBooleanExtraProps
    },
    {
        accessor: 'accessType',
        type: FieldTypeEnum.Select,
        label: 'masterPersonnelForm.accessType',
        required: true,
        testId: personnelMasterIds.addNewPersonnel.inputAccessType,
        isHidden: ({ isFieldHidden, personnelType, entitySettings }) =>
            isFieldHidden('accessType', { personnelType, entitySettings }),
        options: AccessTypeEnum.getOptions({
            translationKey: 'masterPersonnelProperties.accessTypes'
        }),
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'roleMasterId',
        label: 'masterPersonnelForm.role',
        placeholder: 'masterPersonnelForm.rolePlaceholder',
        type: FieldTypeEnum.Select,
        testId: personnelMasterIds.addNewPersonnel.inputRoleMasterId,
        isHidden: ({ isFieldHidden }) => isFieldHidden('roleMasterId'),
        getProcessedValueForField: ({ entity }) => entity.roleMasterId?.id,
        options: ({ field }) => {
            const roles = field.fetchInfo.response;
            return roles
                .filter(role => {
                    if (!role.isDisabled()) {
                        return true;
                    }
                    // include disabled role in option if already assigned
                    return field.value === role.id;
                })
                .map(role => ({
                    value: role.id,
                    label: role.name
                }));
        },
        onChangeHandler: ({ field, entity, value }) => {
            const category = value
                ? RoleId.fromJS({
                      id: value.value,
                      clientId: entity.clientId
                  })
                : null;
            return entity.setIn(field.absoluteAccessor, category);
        },
        fetchInfo: {
            fetcher: ({ clientId, personnelType }) => {
                return fetchRoles({
                    clientId,
                    includeDisabled: true,
                    roleType: personnelType
                });
            },
            params: ({ props }) => {
                return {
                    clientId: props.clientId,
                    personnelType: props.personnelType
                };
            },
            response: null
        },
        extraProps: () => ({
            isSearchable: true
        }),
        required: true
    },
    {
        accessor: 'roleMasterId-link',
        component: GotoRoleId
    },
    {
        accessor: 'timezone',
        label: 'masterPersonnelForm.timezone',
        placeholder: 'masterPersonnelForm.timezonePlaceholder',
        type: FieldTypeEnum.Select,
        testId: personnelMasterIds.addNewPersonnel.inputTimezone,
        isHidden: ({ localeSettings }) =>
            !localeSettings.multiTimeZoneSupportEnabled,
        getProcessedValueForField: ({ entity }) => entity.timezone,
        options: () => CountryHelper.getMinimalTimezoneOptions(),
        onChangeHandler: ({ entity, field, value }) => {
            const updatedTimezone = value?.value;
            return entity.setIn(field.absoluteAccessor, updatedTimezone);
        },
        extraProps: () => ({
            isSearchable: true
        })
    }
];

const SinglePersonnelEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterPersonnel,
    MasterActions: PersonnelActions,
    extraApiParams: {
        include: [IncludeTypeEnums.BusinessUnit, IncludeTypeEnums.IncludeAuth]
    },
    EntityFetchAPI: personnelApi,
    INITIAL_FETCH_FIELDS: ['roleMasterId', 'transporters'],
    FIELDS: PERSONNEL_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'personnelId',
        ENTITY_SETTINGS_NAME: 'personnelSettings',
        preferPutOverPatch: true
    },
    mapEntityStateToProps: ({ state, props }) => {
        const teams = teamsSelector(state, props);
        const countryISOCode = ClientSelectors.getCountryISOCodeFromLocaleSettings(
            state,
            props
        );
        const isEnabled = isMultiTimeZoneSupportEnabled(state, props);
        return {
            teams,
            isMultiTimeZoneSupportEnabled: isEnabled,
            personnelType: props.personnelType,
            defaultCountryISOCode: countryISOCode
        };
    }
});

const SingleEntityEditContainerWrap = props => {
    const isTransporterEnabled = isTransporterPersonnelEnabled(
        props.entitySettings
    );

    return !isTransporterEnabled ? (
        <SinglePersonnelEditContainer
            {...props}
            personnelType={PersonnelTypeEnum.CLIENT}
        />
    ) : props.entityId ? (
        <PersonnelTypeQuerySelector
            personnelId={props.entityId}
            render={type => (
                <SinglePersonnelEditContainer {...props} personnelType={type} />
            )}
        />
    ) : (
        <PersonnelTypeModalSelector
            onDismiss={props.onDismiss}
            render={type => (
                <SinglePersonnelEditContainer {...props} personnelType={type} />
            )}
        />
    );
};

export default SingleEntityEditContainerWrap;
